import { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  ProgressBar,
} from "react-bootstrap";
import "./TestimonialCarousel.css";

// ── Local image imports ──────────────────────────────────────────────────────
import client1 from "./../../utils/images/client1.jpeg";
import client2 from "./../../utils/images/client2.jpeg";
import placeholder from "./../../utils/images/placeholder.jpg";
// ────────────────────────────────────────────────────────────────────────────

const testimonials = [
  {
    id: 1,
    name: "Adel Bashawri",
    role: "CEO",
    company: "4DATA Information Technologies",
    photo: client1,
    comment:
      "Working with the LeadsQResearch team has been a very positive experience. They were highly committed and professional throughout the engagement, with excellent follow-up and communication at every stage. I also appreciated their flexibility and patience in accommodating our requirements and timelines.",
    rating: 5,
    tag: "IT Services",
  },
  {
    id: 2,
    name: "Shubham",
    role: "Founder",
    company: "Cyberpark IT",
    photo: client2,
    comment:
      "We had a great experience working with LeadsQResearch for lead generation services. They consistently delivered high-quality, targeted leads that improved our conversion rate. Their consistent follow-up and dedicated account manager ensured smooth communication and quick resolution of every query. A reliable and results-driven partner for business growth.",
    rating: 5,
    tag: "IT Services",
  },
  {
    id: 3,
    name: "Kamran Babar",
    role: "Founder",
    company: "KMK Engineering Private Limited",
    photo: placeholder,
    comment:
      "LeadsQResearch has provided us with accurate and well-segmented B2B data that perfectly matched our target market. Their research quality and timely delivery helped us improve our outreach results. The team is supportive, proactive, and easy to work with.",
    rating: 5,
    tag: "Engineering",
  },
  {
    id: 4,
    name: "Owner",
    role: "Owner",
    company: "QuantalAI",
    photo: placeholder,
    comment:
      "LeadsQResearch has played a key role in enhancing our prospecting efforts. Their database delivers accurate, up-to-date contact information along with valuable company insights, making our outreach campaigns more targeted and effective. The team is supportive, detail-oriented, and consistently delivers high-quality research data. We confidently recommend LeadsQResearch to businesses seeking trustworthy and results-driven contact discovery services.",
    rating: 5,
    tag: "Artificial Intelligence",
  },
  {
    id: 5,
    name: "Client",
    role: "Owner",
    company: "Staffing & Recruitment",
    photo: placeholder,
    comment:
      "LeadsQResearch has been a reliable partner for our B2B lead generation and research needs. Their data accuracy, structured email campaigns, and professional approach have delivered consistent results for us.",
    rating: 5,
    tag: "Staffing & Recruitment",
  },
];

const CARDS_PER_PAGE = 3;
const AUTO_PLAY_INTERVAL = 30000;

const StarRating = ({ rating, max = 5 }) => (
  <div className="tc-stars" aria-label={`${rating} out of ${max} stars`}>
    {Array.from({ length: max }).map((_, i) => (
      <span key={i} className={`tc-star ${i < rating ? "tc-star--filled" : ""}`}>
        ★
      </span>
    ))}
  </div>
);

export default function TestimonialCarousel() {
  const [currentPage, setCurrentPage] = useState(0);
  const [animClass, setAnimClass] = useState("");
  const [autoPlay, setAutoPlay] = useState(true);
  const [progress, setProgress] = useState(0);
  const [flipped, setFlipped] = useState({});
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const totalPages = Math.ceil(testimonials.length / CARDS_PER_PAGE);

  const navigate = (newPage, dir) => {
    setAnimClass(`tc-anim--${dir}`);
    setTimeout(() => {
      setCurrentPage(newPage);
      setFlipped({});        // reset any flipped cards on page change
      setAnimClass("");
      setProgress(0);
    }, 320);
  };

  const handlePrev = () => navigate((currentPage - 1 + totalPages) % totalPages, "prev");
  const handleNext = () => navigate((currentPage + 1) % totalPages, "next");

  const handleDotClick = (i) => {
    if (i !== currentPage) navigate(i, i > currentPage ? "next" : "prev");
  };

  const toggleFlip = (id, e) => {
    e.stopPropagation();
    setFlipped((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Auto-play with progress bar
  useEffect(() => {
    clearInterval(timerRef.current);
    clearInterval(progressRef.current);
    if (!autoPlay) { setProgress(0); return; }

    setProgress(0);
    const step = 100 / (AUTO_PLAY_INTERVAL / 100);

    progressRef.current = setInterval(() => {
      setProgress((p) => Math.min(p + step, 100));
    }, 100);

    timerRef.current = setInterval(() => {
      setCurrentPage((cp) => {
        const next = (cp + 1) % totalPages;
        setAnimClass("tc-anim--next");
        setTimeout(() => setAnimClass(""), 320);
        setProgress(0);
        setFlipped({});
        return next;
      });
    }, AUTO_PLAY_INTERVAL);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(progressRef.current);
    };
  }, [autoPlay, totalPages]);

  const pageCards = testimonials.slice(
    currentPage * CARDS_PER_PAGE,
    currentPage * CARDS_PER_PAGE + CARDS_PER_PAGE
  );

  return (
    <section className="tc-section">
      <Container>

        {/* ── Header ── */}
        <Row className="justify-content-center mb-2">
          <Col xs="auto">
            <Badge className="tc-badge">✦ Client Stories</Badge>
          </Col>
        </Row>
        <Row className="justify-content-center text-center mb-2">
          <Col md={8}>
            <h2 className="tc-title">Voices That Matter</h2>
            <p className="tc-subtitle">
              Honest feedback from the teams we've been proud to work with.
            </p>
          </Col>
        </Row>

        {/* ── Controls ── */}
        <Row className="justify-content-center align-items-center mb-4 g-2">
          <Col xs="auto">
            <Button
              variant={autoPlay ? "warning" : "outline-secondary"}
              size="sm"
              className="tc-ctrl-btn"
              onClick={() => setAutoPlay((a) => !a)}
            >
              {autoPlay ? "⏸ Pause" : "▶ Play"}
            </Button>
          </Col>
          <Col xs={5} sm={4} md={3}>
            <ProgressBar
              now={progress}
              className={`tc-progress ${!autoPlay ? "opacity-0" : ""}`}
              variant="warning"
            />
          </Col>
          <Col xs="auto" className="tc-page-info">
            <span className="tc-page-current">{currentPage + 1}</span>
            <span className="tc-page-sep"> / </span>
            <span className="tc-page-total">{totalPages}</span>
          </Col>
        </Row>

        {/* ── Stage ── */}
        <div className="tc-stage">
          <Button variant="outline-light" className="tc-arrow" onClick={handlePrev} aria-label="Previous">‹</Button>

          <Row className={`tc-track g-3 ${animClass}`}>
            {pageCards.map((t, i) => (
              <Col key={t.id} xs={12} md={4} style={{ "--i": i }}>
                <div className="tc-flip-container">

                  {/* ── Front ── */}
                  <Card className="tc-card tc-card--front">
                    <Card.Body className="tc-card__body">

                      {/* Profile at top — centered column */}
                      <div className="tc-card__profile">
                        <img src={t.photo} alt={t.name} className="tc-avatar-top" />
                        <div className="tc-card__profile-info">
                          <div className="tc-card__name">{t.name}</div>
                          <div className="tc-card__role">
                            {t.role} · <span className="tc-card__company">{t.company}</span>
                          </div>
                          <StarRating rating={t.rating} />
                          <div className="tc-card__tag-row">
                            <Badge className="tc-tag">{t.tag}</Badge>
                          </div>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="tc-card__divider" />

                      {/* Quote + comment */}
                      <div className="tc-quote-mark">"</div>
                      <Card.Text className="tc-card__comment">
                        {t.comment}
                      </Card.Text>

                    </Card.Body>
                  </Card>

                  {/* Back face removed — no longer needed */}
                  <Card className="tc-card tc-card--back" style={{ display: 'none' }} />

                </div>
              </Col>
            ))}
          </Row>

          <Button variant="outline-light" className="tc-arrow" onClick={handleNext} aria-label="Next">›</Button>
        </div>

        {/* ── Dots ── */}
        <Row className="justify-content-center mt-4">
          <Col xs="auto" className="d-flex gap-2 align-items-center">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                className={`tc-dot ${i === currentPage ? "tc-dot--active" : ""}`}
                onClick={() => handleDotClick(i)}
                aria-label={`Go to page ${i + 1}`}
              />
            ))}
          </Col>
        </Row>

      </Container>
    </section>
  );
}
